import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import { IcCrossedChain } from '@/assets/images';
import {
  OpenInNewTabAction,
  canOpenPath,
} from '@/components/OpenInNewTabAction';
import { IKase } from '@/models';
import { useUserStore } from '@/store';

type ColumnGenerator = () => Partial<GridColDef>;
type ColumnMap = Record<string, ColumnGenerator>;

export const InternalAffairsRelatedRecordsCols = (
  onUnlink?: (row: any) => void,
): ColumnMap => ({
  recordNumber: () => ({
    field: 'recordNumber',
    headerName: 'Record number',
    minWidth: 180,
    filterOperators: getGridStringOperators().filter(
      (item) => item.value !== 'isAnyOf',
    ),
    valueGetter: (_value: any, row: any) => row.number || '',
    renderCell: (params: GridRenderCellParams) => {
      return params.row.number || '/';
    },
  }),
  agency: () => ({
    field: 'agency',
    headerName: 'Agency',
    minWidth: 200,
    filterOperators: getGridStringOperators().filter(
      (item) => item.value !== 'isAnyOf',
    ),
    valueGetter: (_value: any, row: any) => {
      const agency = row.agency;
      if (!agency) return '';
      return typeof agency === 'string' ? agency : agency.name || '';
    },
    renderCell: (params: GridRenderCellParams) => {
      const agency = params.row.agency;
      if (!agency) return '/';
      return typeof agency === 'string' ? agency : agency.name || '/';
    },
  }),
  createdDate: () => ({
    field: 'createdDate',
    headerName: 'Created date',
    minWidth: 180,
    type: 'dateTime',
    filterOperators: getGridDateOperators(true),
    valueGetter: (_value: any, row: any) =>
      row.createdAt ? new Date(row.createdAt) : null,
    valueFormatter: (value: Date | string | null) => {
      if (!value) return '/';
      const date = value instanceof Date ? value : new Date(value);
      if (isNaN(date.getTime())) return '/';
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yyyy = date.getFullYear();
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
    },
  }),
  actions: () => ({
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    filterable: false,
    width: 100,
    cellClassName: 'actions-cell',
    renderCell: (params: GridRenderCellParams) => {
      const { user, account } = useUserStore();
      const record = params.row;
      const isKase = Boolean((record as IKase).address);
      const url = isKase
        ? `/cases/${record._id}/overview`
        : `/cfs/${record._id}`;

      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <OpenInNewTabAction
            path={url}
            canOpen={canOpenPath(url, user, account)}
          />
          {onUnlink && (
            <Tooltip title="Unlink">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onUnlink(record);
                }}
                sx={{ height: '31px', width: '31px' }}
                aria-label="unlink"
              >
                <Box sx={{ '& path': { strokeWidth: 2 } }}>
                  <IcCrossedChain style={{ width: 24, height: 24 }} />
                </Box>
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      );
    },
  }),
});
